import React, { useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import Modal, { Styles } from "react-modal";
import IWorkflow from "../../../../models/IWorkflow";
import SupplierContext from "../../../../contexts/SupplierContext";
import UserContext from "../../../../contexts/UserContext";
import IGetWorkflowsDTO from "../../../../models/DTO/IGetWorkflowsDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IOnGetWorkflowsDTO from "../../../../models/DTO/IOnGetWorkflowsDTO";
import IOnProcessWorkflowDTO from "../../../../models/DTO/IOnProcessWorkflowDTO";
import { EditWorkflowModal } from "../EditWorkflowModal/EditWorkflowModal";
import OwButton from "../../../shared/OwButton/OwButton";
import { MultiWorkflowEditor } from "../MultiWorkflowEditor/MultiWorkflowEditor";
import IProcessWorkflowDTO from "../../../../models/DTO/IProcessWorkflowDTO";
import BetaBadge from "../../../shared/BetaBadge/BetaBadge";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import { Socket } from "socket.io-client";

type Props = {
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    closeFn: () => void;
    setDockComponent: (dockComponent: any, dockHeight?: number) => void;
    unsetDock: () => void;
};

export const WorkflowSidebar: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const getModalStyle = (): Styles => {
        return {
            content: {
                top: "10px",
                left: "10px",
                right: "10px",
                bottom: "10px",
                color: "var(--main-text-color)",
                backgroundColor: "var(--main-bg-color)",
                border: "1px solid var(--minor-bg-color)",
            },
            overlay: {
                zIndex: 100,
            },
        };
    };

    const [workflows, setWorkflows] = useState<IWorkflow[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalComponent, setModalComponent] = useState<any>(null);
    const [modalStyle, setModalStyle] = useState<Styles>(getModalStyle());

    const getWorkflows = () => {
        const payload: IGetWorkflowsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.WorkflowsGetWorkflows, payload);

        props.setActivityIndicator(true);
    };

    const onGetWorkflows = (data: IOnGetWorkflowsDTO) => {
        props.setActivityIndicator(false);
        setWorkflows(data.workflows);
    };

    const processWorkflow = (workflow: IWorkflow, deleteFlag?: boolean) => {
        const payload: IProcessWorkflowDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            workflow,
            deleteFlag,
        };

        props.socket.emit(EndpointsEnum.WorkflowsProcessWorkflow, payload);
    };

    const onProcessWorkflow = (data: IOnProcessWorkflowDTO) => {
        getWorkflows();
        loadUser();
    };

    const closeModal = (refreshData?: boolean) => {
        setModalComponent(null);
        setModalIsOpen(false);
    };

    const loadUser = () => {
        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    const createNewWorkflow = () => {
        const component = <EditWorkflowModal closeModalFn={closeModal} socket={props.socket} notifyFn={props.notifyFn} />;

        setModalComponent(component);
        setModalIsOpen(true);
    };

    useEffect(() => {
        props.socket.on(EndpointsEnum.WorkflowsOnGetWorkflows, onGetWorkflows);
        props.socket.on(EndpointsEnum.WorkflowsOnProcessWorkflow, onProcessWorkflow);

        getWorkflows();

        return () => {
            props.socket.removeListener(EndpointsEnum.WorkflowsOnGetWorkflows, onGetWorkflows);
            props.socket.removeListener(EndpointsEnum.WorkflowsOnProcessWorkflow, onProcessWorkflow);
        };
    }, []);

    const closeDock = () => {
        props.unsetDock();
    };

    const manageWorkflow = (workflow: IWorkflow) => {
        const dockComponent = (
            <MultiWorkflowEditor
                setActivityIndicator={props.setActivityIndicator}
                socket={props.socket}
                notifyFn={props.notifyFn}
                workflow={workflow}
                closeDock={closeDock}
            />
        );

        props.setDockComponent(dockComponent, 0.95);
    };

    const closeSidebar = () => {
        props.closeFn();
    };

    const editWorkflow = (workflow: IWorkflow) => {
        const component = (
            <EditWorkflowModal closeModalFn={closeModal} socket={props.socket} notifyFn={props.notifyFn} workflow={workflow} />
        );

        setModalComponent(component);
        setModalIsOpen(true);
    };

    return (
        <div className={"WorkflowSidebar p-3"}>
            <div className={"d-flex justify-content-end"}>
                <div>
                    <OwButton centerIconClass={"fas fa-times"} fn={closeSidebar} className={"btn-danger"} />
                </div>
            </div>
            <div className={"d-flex align-items-center"}>
                <OwTitle title={"workflows"} />

                <div className={"ms-3"}>
                    <OwButton className={"btn-warning"} leftIconClass={"fas fa-plus"} title={"create"} fn={createNewWorkflow} />
                </div>
            </div>

            <div>
                {workflows.map((workflow) => {
                    return (
                        <div key={workflow.guid} className={"d-flex mt-2 align-items-center"}>
                            <i className={"fas fa-trash-alt text-danger pointer me-2"} onClick={() => processWorkflow(workflow, true)} />
                            <i className={"fas fa-pencil text-primary pointer me-2"} onClick={() => editWorkflow(workflow)} />
                            <div className={"d-flex"}>
                                <div className={"me-2"}>{workflow.name}</div>
                                <div className={"me-2"}>
                                    {workflow.id.toString() === supplierModel.default_workflow_id?.toString() ? (
                                        <BetaBadge title={"default"} />
                                    ) : null}
                                </div>
                                <div className={"me-2"}>
                                    <OwButton title={"manage_workflow"} fn={() => manageWorkflow(workflow)} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div>
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel="Orderwerks"
                    shouldCloseOnOverlayClick={false}
                    className={"modalContentClass"}
                    overlayClassName={"modalOverlayClass"}
                    style={modalStyle}>
                    {modalComponent}
                </Modal>
            </div>
        </div>
    );
};
